/**
 * Small fetch wrapper with a default timeout.
 * Prevents UI from hanging forever on stalled network requests.
 */

import { recordSalonUserActivity } from './salonSessionIdle';

const DEFAULT_TIMEOUT_MS = 20000;
const SALON_LOGIN_SESSION_TOKEN_KEY = 'salon_login_session_token';
const SALON_LOGIN_DEVICE_ID_KEY = 'salon_login_device_id';

function isSalonAuthApiRequest(input) {
  const raw = typeof input === 'string' ? input : String(input?.url || '');
  if (!raw.includes('/api/salon/')) return false;
  if (raw.includes('/api/salon/login')) return false;
  if (raw.includes('/api/salon/logout')) return false;
  if (raw.includes('/api/salon/auth/validate')) return false;
  return true;
}

function ensureSalonDeviceId() {
  try {
    const existing = sessionStorage.getItem(SALON_LOGIN_DEVICE_ID_KEY);
    if (existing && String(existing).trim()) return String(existing).trim();
    const generated =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `salon-device-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SALON_LOGIN_DEVICE_ID_KEY, generated);
    return generated;
  } catch (_) {
    return `salon-device-fallback-${Date.now()}`;
  }
}

function isSalonApiRequest(input) {
  const raw = typeof input === 'string' ? input : String(input?.url || '');
  return raw.includes('/api/salon/');
}

function withSalonAuthHeaders(input, initObj) {
  if (initObj?.skipSalonAuthHeaders) {
    return initObj;
  }

  if (!isSalonApiRequest(input)) return initObj;

  const next = { ...(initObj || {}) };
  const headers = new Headers(next.headers || {});

  try {
    const loginToken = sessionStorage.getItem(SALON_LOGIN_SESSION_TOKEN_KEY);
    const deviceId = ensureSalonDeviceId();
    if (loginToken && !headers.has('X-Salon-Login-Token')) {
      headers.set('X-Salon-Login-Token', String(loginToken));
    }
    if (deviceId && !headers.has('X-Salon-Device-Id')) {
      headers.set('X-Salon-Device-Id', String(deviceId));
    }
  } catch (_) {
    // no-op when storage is unavailable
  }

  next.headers = headers;
  return next;
}
 
/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit & { timeoutMs?: number }} [init]
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(input, init = {}) {
  const enhancedInit = withSalonAuthHeaders(input, init || {});
  const { timeoutMs, signal, skipSalonAuthHeaders, ...rest } = enhancedInit || {};
  const ms = Number.isFinite(timeoutMs) ? timeoutMs : DEFAULT_TIMEOUT_MS;
 
  // If AbortController isn't available (older browsers), fall back to plain fetch.
  if (typeof AbortController === 'undefined') {
    const response = await fetch(input, { ...rest, signal });
    if (response.ok && isSalonAuthApiRequest(input)) {
      recordSalonUserActivity();
    }
    return response;
  }
 
  const controller = new AbortController();
  let didTimeout = false;
  const timeoutId = setTimeout(() => {
    didTimeout = true;
    controller.abort();
  }, Math.max(0, ms));
 
  // If caller provided a signal, abort when either one aborts.
  if (signal) {
    if (signal.aborted) controller.abort();
    else {
      const onAbort = () => controller.abort();
      signal.addEventListener('abort', onAbort, { once: true });
    }
  }
 
  try {
    const response = await fetch(input, { ...rest, signal: controller.signal });
    if (response.ok && isSalonAuthApiRequest(input)) {
      recordSalonUserActivity();
    }
    return response;
  } catch (err) {
    // Provide a clearer error for timeout-caused aborts (distinguish from user navigation abort).
    if (didTimeout && (err?.name === 'AbortError' || err?.code === 20)) {
      const e = new Error(`Request timed out after ${ms}ms`);
      e.name = 'TimeoutError';
      e.cause = err;
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

